/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { apiClient } from '../../services/api';
import { Participant, Bid } from '../../types';
import './ReassignBidModal.css';

interface ReassignBidModalProps {
  bid: Bid;
  onClose: () => void;
  onSuccess: () => void;
}

const ReassignBidModal: React.FC<ReassignBidModalProps> = ({ bid, onClose, onSuccess }) => {
  const [eligibleUsers, setEligibleUsers] = useState<Participant[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadEligibleUsers();
  }, []);

  const loadEligibleUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.getAllParticipants();

      if (!response.success || !response.participants) {
        setError('Failed to load users');
        return;
      }

      // Filter to only USER type participants who are not inactive
      // The backend will enforce role-based filtering
      const users = response.participants.filter(
        p => p.type === 'USER' && !p.userInactive && p.id !== bid.author?.id
      );

      setEligibleUsers(users);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const response = await apiClient.reassignBid(bid.id, {
        newAuthorId: Number(selectedUserId)
      });

      if (response.success) {
        onSuccess();
      } else {
        setError(response.error || 'Failed to reassign bid');
      }
    } catch (err: any) {
      console.error('Error reassigning bid:', err);
      setError(err.response?.data?.error || err.message || 'Failed to reassign bid');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Reassign Bid</h2>
          <button className="close-button" onClick={onClose} disabled={isSaving}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="bid-info">
            <p><strong>Bid:</strong> {bid.name}</p>
            {bid.author && (
              <p>
                <strong>Current Author:</strong> {bid.author.firstName} {bid.author.lastName} ({bid.author.username})
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="newAuthor">New Author *</label>
              {isLoading ? (
                <p>Loading users...</p>
              ) : eligibleUsers.length === 0 ? (
                <p className="no-users-message">No eligible users available for reassignment</p>
              ) : (
                <select
                  id="newAuthor"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : '')}
                  required
                  disabled={isSaving}
                >
                  <option value="">Select a user</option>
                  {eligibleUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.username})
                      {user.companyName && ` - ${user.companyName}`}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving || !selectedUserId || isLoading}
                className="submit-button"
              >
                {isSaving ? 'Reassigning...' : 'Reassign Bid'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReassignBidModal;
